<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Top Performers',
  },
});

// Max value for scaling bars
const maxValue = computed(() => {
  if (!props.data || props.data.length === 0) return 1;
  return Math.max(...props.data.map(d => d.conversations || 0), 1);
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <!-- Header -->
    <div class="mb-4">
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="data.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-users text-3xl mb-2" />
      <span class="text-sm">Nenhum dado de agente disponível</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex items-end gap-4 h-64">
      <div
        v-for="(agent, index) in data"
        :key="index"
        class="flex-1 flex flex-col items-center"
      >
        <!-- Bars -->
        <div class="flex gap-1 w-full justify-center items-end h-48">
          <!-- Conversations bar -->
          <div
            class="w-8 bg-n-blue-9 rounded-t-md transition-all duration-300"
            :style="{
              height: `${(agent.conversations / maxValue) * 100}%`,
              minHeight: agent.conversations > 0 ? '8px' : '0',
            }"
            :title="`${agent.conversations} conversas`"
          />
          <!-- Resolved bar -->
          <div
            class="w-8 bg-n-teal-9 rounded-t-md transition-all duration-300"
            :style="{
              height: `${(agent.resolved / maxValue) * 100}%`,
              minHeight: agent.resolved > 0 ? '8px' : '0',
            }"
            :title="`${agent.resolved} resolvidas`"
          />
        </div>

        <!-- Agent name -->
        <div class="mt-2 text-center">
          <span class="text-xs text-n-slate-11 truncate block max-w-20">
            {{ agent.name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div
      class="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-n-weak"
    >
      <div class="flex items-center gap-2">
        <span class="w-3 h-3 rounded-sm bg-n-blue-9" />
        <span class="text-xs text-n-slate-11">Conversas</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="w-3 h-3 rounded-sm bg-n-teal-9" />
        <span class="text-xs text-n-slate-11">Resolvidas</span>
      </div>
    </div>
  </div>
</template>
